// src/services/productService.js
import { db } from "../firebase";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  or // ✅ YENİ: Firebase OR operatörü eklendi
} from "firebase/firestore";

// Yardımcı Fonksiyon: Firestore verisini düzenler
const mapDoc = (doc) => ({
  id: doc.id,
  ...doc.data()
});

export const productService = {
  // --- MÜŞTERİ TARAFI ---
  getAllProducts: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "products"));
      return querySnapshot.docs.map(mapDoc);
    } catch (error) {
      console.error("Error getting all products:", error);
      throw error;
    }
  },

  // ✅ Kategoriye Göre Ürünleri Getir (Shop Sayfası İçin)
  getProductsByCategory: async (categoryTop) => {
    try {
      if (!categoryTop) return []; 
      
      const q = query(
        collection(db, "products"), 
        where("categoryTop", "==", categoryTop)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(mapDoc);
    } catch (error) {
      console.error("Kategori ürünleri hatası:", error);
      throw error;
    }
  },

  // ✅ Tek Ürün Detayı Getir (ProductDetail Sayfası İçin)
  getProductById: async (id) => {
    try {
      const docRef = doc(db, "products", id);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) return null;
      return mapDoc(snapshot);
    } catch (error) {
      console.error("Ürün detay hatası:", error);
      throw error;
    }
  },

  // Slug (SEO Linki) ile ürünü saniyesinde bulmamızı sağlayan yeni fonksiyon
  getProductBySlug: async (slug) => {
    try {
      const q = query(collection(db, "products"), where("slug", "==", slug));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      return null;
    } catch (error) {
      console.error("Slug ile ürün çekme hatası:", error);
      return null;
    }
  },

  // ✅ Varyantları Getir (Renk Grupları vb.) - OR OPERATÖRÜ İLE OPTİMİZE EDİLDİ
  getProductVariants: async (groupKey) => {
    try {
      if (!groupKey) return [];
      
      // Tek sorguda hem variantGroupId hem de modelCode kontrolü yapıyoruz.
      // Bu sayede veritabanına iki kez gitmek yerine tek seferde, hızlıca veriyi çekiyoruz.
      const q = query(
        collection(db, "products"),
        or(
          where("variantGroupId", "==", groupKey),
          where("modelCode", "==", groupKey)
        )
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(mapDoc);
    } catch (error) {
      console.error("Varyant getirme hatası:", error);
      return [];
    }
  },

  getDiscountedProducts: async () => {
    try {
      const q = query(
        collection(db, "products"),
        where("discountActive", "==", true)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(mapDoc);
    } catch (error) {
      console.error("Error getting discounted products:", error);
      throw error;
    }
  },

  getProductStock: async (id, size = null) => {
    try {
      const docRef = doc(db, "products", id);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) return 0;
      const pData = snapshot.data();

      if (Array.isArray(pData.stock) && pData.stock.length > 0) {
        if (size) {
          const entry = pData.stock.find((s) => String(s.size) === String(size));
          return entry ? Number(entry.qty || 0) : 0;
        }
        return pData.stock.reduce((s, r) => s + (Number(r.qty || 0)), 0);
      }
      if (pData.stockBySize && typeof pData.stockBySize === "object") {
        if (size) return Number(pData.stockBySize[size] || 0);
        return Object.values(pData.stockBySize).reduce((s, v) => s + (Number(v || 0)), 0);
      }
      const numericFields = ["availableStock", "stockCount", "stock", "quantity", "qty"];
      for (const f of numericFields) {
        if (Object.prototype.hasOwnProperty.call(pData, f) && typeof pData[f] === "number") {
          return Number(pData[f] || 0);
        }
      }
      return 0; 
    } catch (error) {
      console.error("Stok kontrol hatası:", error);
      return 0;
    }
  },

  // --- ADMIN TARAFI ---
  addProduct: async (productData) => {
    try {
      const docRef = await addDoc(collection(db, "products"), {
        ...productData,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error("Ürün ekleme hatası:", error);
      throw error;
    }
  },

  deleteProduct: async (productId) => {
    try {
      await deleteDoc(doc(db, "products", productId));
    } catch (error) {
      console.error("Ürün silme hatası:", error);
      throw error;
    }
  },

  updateProduct: async (productId, updatedData) => {
    try {
      const docRef = doc(db, "products", productId);
      await updateDoc(docRef, updatedData);
    } catch (error) {
      console.error("Ürün güncelleme hatası:", error);
      throw error;
    }
  },

  checkSlugUniqueness: async (slug) => {
     const q = query(collection(db, "products"), where("slug", "==", slug));
     const snap = await getDocs(q);
     return snap.empty;
  },

  updateStock: async (productId, stockData, userIdentifier) => {
    try {
      const docRef = doc(db, "products", productId);
      const payload = {
        ...stockData,
        lastStockUpdatedAt: serverTimestamp(),
        lastStockUpdatedBy: userIdentifier || "admin"
      };
      await updateDoc(docRef, payload);
    } catch (error) {
      console.error("Stok güncelleme hatası:", error);
      throw error;
    }
  },

  // --- İNDİRİM YÖNETİMİ ---
  getProductsForDiscount: async ({ categoryKey, onlyDiscounted, lastDoc, pageSize = 50 }) => {
    try {
      const col = collection(db, "products");
      const clauses = [];

      if (categoryKey && categoryKey !== "Hepsi") {
        if (categoryKey.includes("/")) {
          clauses.push(where("categoryKey", "==", categoryKey));
        } else {
          clauses.push(where("categoryTop", "==", categoryKey));
        }
      }
      if (onlyDiscounted) {
        clauses.push(where("discountActive", "==", true));
      }

      clauses.push(orderBy("createdAt", "desc"));

      if (lastDoc) {
        clauses.push(startAfter(lastDoc));
      }

      const q = query(col, ...clauses, limit(pageSize));
      const snap = await getDocs(q);
      
      const items = snap.docs.map(mapDoc);
      const lastVisible = snap.docs[snap.docs.length - 1];
      const hasMore = snap.docs.length === pageSize;

      return { items, lastVisible, hasMore };
    } catch (error) {
      console.error("İndirim ürünleri çekme hatası:", error);
      throw error;
    }
  },

  updateProductDiscount: async (productId, discountData) => {
    try {
      const docRef = doc(db, "products", productId);
      await updateDoc(docRef, discountData);
    } catch (error) {
      console.error("İndirim güncelleme hatası:", error);
      throw error;
    }
  },

  batchUpdateDiscounts: async (updates) => {
    try {
      const batch = writeBatch(db);
      updates.forEach(({ id, data }) => {
        const docRef = doc(db, "products", id);
        batch.update(docRef, data);
      });
      await batch.commit();
    } catch (error) {
      console.error("Toplu indirim hatası:", error);
      throw error;
    }
  }
};